import posthog from 'posthog-js';

export type ConsentStatus = 'pending' | 'granted' | 'denied';

export const ANALYTICS_CONSENT_UPDATED_EVENT = 'merchmuse:analytics-consent-updated';
export const OPEN_COOKIE_SETTINGS_EVENT = 'merchmuse:open-cookie-settings';

const CONSENT_COOKIE_BASE_NAME = 'mm_analytics_consent';
const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

function getConsentCookieName(): string {
  return window.location.hostname.includes('staging')
    ? `${CONSENT_COOKIE_BASE_NAME}_staging`
    : CONSENT_COOKIE_BASE_NAME;
}

function getConsentCookieDomain(): string | null {
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return null;
  return hostname === 'merchmuse.art' || hostname.endsWith('.merchmuse.art')
    ? '.merchmuse.art'
    : null;
}

function readCookie(name: string): string | null {
  const encodedName = `${encodeURIComponent(name)}=`;
  const entry = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(encodedName));

  if (!entry) return null;
  return decodeURIComponent(entry.slice(encodedName.length));
}

function writeConsentCookie(status: Exclude<ConsentStatus, 'pending'>): void {
  let cookie = `${encodeURIComponent(getConsentCookieName())}=${encodeURIComponent(status)}`;
  cookie += `; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;

  const domain = getConsentCookieDomain();
  if (domain) cookie += `; Domain=${domain}`;
  if (window.location.protocol === 'https:') cookie += '; Secure';

  document.cookie = cookie;
}

export function readConsentStatus(): ConsentStatus {
  const status = readCookie(getConsentCookieName());
  if (status === 'granted' || status === 'denied') {
    return status;
  }
  return 'pending';
}

export function isConsentPending(): boolean {
  return readConsentStatus() === 'pending';
}

export function isAnalyticsConsentGranted(): boolean {
  return readConsentStatus() === 'granted';
}

export function isBasicAnalyticsCaptureAllowed(): boolean {
  return readConsentStatus() !== 'pending';
}

export function isAppPageviewCaptureAllowed(): boolean {
  return isBasicAnalyticsCaptureAllowed();
}

export function applyAnalyticsConsent(status: Exclude<ConsentStatus, 'pending'>): void {
  if (status === 'granted') {
    posthog.opt_in_capturing();
    return;
  }

  posthog.reset();
  posthog.opt_out_capturing();
}

export function syncAnalyticsConsentFromCookie(): ConsentStatus {
  const status = readConsentStatus();
  if (status === 'granted' || status === 'denied') {
    applyAnalyticsConsent(status);
  }
  return status;
}

export function updateAnalyticsConsent(status: Exclude<ConsentStatus, 'pending'>): void {
  writeConsentCookie(status);
  applyAnalyticsConsent(status);
  emitAnalyticsConsentUpdated(status);
}

export function emitAnalyticsConsentUpdated(status: Exclude<ConsentStatus, 'pending'>): void {
  window.dispatchEvent(
    new CustomEvent(ANALYTICS_CONSENT_UPDATED_EVENT, {
      detail: { status },
    }),
  );
}

export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
